import {
  PRODUCT_ONLY_FIELDS,
  SHARED_FIELDS,
  VARIANT_ONLY_FIELDS,
  isBuiltinFieldKey,
  isCustomFieldMapping,
  parseScopedCustomMapping,
} from './importFieldRegistry';

export {
  PRODUCT_ONLY_FIELDS,
  VARIANT_ONLY_FIELDS,
  SHARED_FIELDS,
  isCustomFieldMapping,
  parseScopedCustomMapping,
} from './importFieldRegistry';

/** Product catalog fields (excludes sku and shared commerce fields). */
export const BUILTIN_PRODUCT_FIELDS = PRODUCT_ONLY_FIELDS.filter(
  (field) => field !== 'sku',
);

/** Variant scalar fields for batch import mapping. */
export const BUILTIN_VARIANT_FIELDS = VARIANT_ONLY_FIELDS;

export const BUILTIN_FIELD_SKU = 'sku';

export const BUILTIN_FIELD_TITLE = 'title';

export const BUILTIN_FIELD_RECORD_ID = 'recordId';

export const BUILTIN_REQUIRED_FIELDS = [BUILTIN_FIELD_SKU, BUILTIN_FIELD_TITLE];

/** Matches batchImport importMode `update` — title not required for create. */
export const IMPORT_MODE_UPDATE = 'update';

/** Matches batchImport importMode `addAndUpdate`. */
export const IMPORT_MODE_ADD_AND_UPDATE = 'addAndUpdate';

export function isTitleRequiredForImportMode(importMode) {
  return importMode !== IMPORT_MODE_UPDATE;
}

/** Update and Add & Update may identify a row by SKU or Toriut Record ID. Add may not. */
export function identityAcceptsRecordId(importMode) {
  return importMode === IMPORT_MODE_UPDATE || importMode === IMPORT_MODE_ADD_AND_UPDATE;
}

/**
 * Builtin fields that must be mapped before leaving the mapping step.
 * Title is optional for update-only imports (matches batchImport create-only title rule).
 * Update-only identity is SKU or Toriut Record ID — use areRequiredBuiltinFieldsMapped.
 */
export function getRequiredBuiltinMappingFields(importMode) {
  if (isTitleRequiredForImportMode(importMode)) {
    return BUILTIN_REQUIRED_FIELDS;
  }
  return [BUILTIN_FIELD_SKU];
}

export function hasRowIdentity(payload, importMode) {
  if (payload?.sku) return true;
  return identityAcceptsRecordId(importMode) && Boolean(payload?.recordId);
}

/**
 * Mapping-step gate.
 * add needs SKU + Title.
 * addAndUpdate needs Record ID, or SKU + Title.
 * update needs SKU or Record ID.
 */
export function areRequiredBuiltinFieldsMapped(importMode, mappedSet) {
  if (importMode === IMPORT_MODE_UPDATE) {
    return mappedSet.has(BUILTIN_FIELD_SKU) || mappedSet.has(BUILTIN_FIELD_RECORD_ID);
  }
  if (importMode === IMPORT_MODE_ADD_AND_UPDATE && mappedSet.has(BUILTIN_FIELD_RECORD_ID)) {
    return true;
  }
  return getRequiredBuiltinMappingFields(importMode).every((field) => mappedSet.has(field));
}

export const BUILTIN_FIELD_STATUS = 'status';

/**
 * Builtin fields that hide Empty cell behavior in mapping UI and always use Skip.
 */
export const BUILTIN_HIDE_EMPTY_CELL_BEHAVIOR_FIELDS = new Set([
  BUILTIN_FIELD_SKU,
  BUILTIN_FIELD_TITLE,
  BUILTIN_FIELD_STATUS,
  'recordId',
  'mainImage',
]);

/** Builtin fields that support Add / Overwrite merge behaviors. */
export const BUILTIN_MERGE_FIELDS = new Set(['keywords', 'media']);

export function isBuiltinField(value) {
  return isBuiltinFieldKey(value);
}

/** Shared cap for import file size and batch import JSON payload. */
export const IMPORT_SIZE_LIMIT_BYTES = 95 * 1024 * 1024;

/** Data rows only — the header row is not counted. */
export const IMPORT_ROW_LIMIT = 50000;

export function isOverImportSizeLimit(byteLength) {
  return byteLength > IMPORT_SIZE_LIMIT_BYTES;
}

export function isOverImportRowLimit(dataRowCount) {
  return dataRowCount > IMPORT_ROW_LIMIT;
}

export function getImportDataRowCount(rowCount) {
  if (!Number.isFinite(rowCount) || rowCount <= 0) {
    return 0;
  }
  return Math.max(0, rowCount - 1);
}

export function getImportFileType(file) {
  const name = String(file?.name || '');
  const i = name.lastIndexOf('.');
  const ext = i >= 0 ? name.slice(i + 1).toLowerCase() : '';
  if (ext === 'csv' || ext === 'xlsx') {
    return ext;
  }
  return undefined;
}

export function getJsonUtf8ByteLength(value) {
  return new TextEncoder().encode(JSON.stringify(value)).length;
}
